import { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getWabaBlockStatus } from '../../services/contacts-day';
import { CustomerContactCreditContext } from 'src/context/CustomerContactCredit/context';
import { customerExpendCreditsPercentage } from '../../components/chat/content/contexts/customerChannelBalance/useBalanceService/contactDayPercent/allChannels';

interface UseWabaBlockAt100CreditsProps {
  channelId?: number;
}

export function useWabaBlockAt100Credits({
  channelId,
}: UseWabaBlockAt100CreditsProps) {
  const [isWabaBlockEnabled, setIsWabaBlockEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const { data: creditsData } = useContext(CustomerContactCreditContext);

  const customerId = useSelector(
    (state: any) => state?.general?.current_customer_id
  );

  // Busca o status de bloqueio do cliente
  const fetchBlockStatus = useCallback(async () => {
    if (!customerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await getWabaBlockStatus(customerId);
      const blockEnabled = data?.block_enabled ?? false;
      setIsWabaBlockEnabled(blockEnabled);
    } catch (error) {
      setIsWabaBlockEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchBlockStatus();
  }, [fetchBlockStatus]);

  // Verifica se o canal atingiu 100% dos créditos - recalcula quando channelId ou creditsData mudam
  const isAt100PercentCredits = useMemo(() => {
    if (!channelId || !creditsData) {
      return false;
    }

    const channelPercentage = customerExpendCreditsPercentage(
      channelId,
      creditsData
    );

    if (!channelPercentage.hasPercentage) {
      return false;
    }

    return (channelPercentage.percentageValue ?? 0) >= 100;
  }, [channelId, creditsData]);

  // Verifica se o canal é WABA - recalcula quando channelId ou creditsData mudam
  const isWabaChannel = useMemo(() => {
    if (!channelId || !creditsData) {
      return false;
    }

    const channelData = (creditsData as any)[channelId];
    return channelData?.isWaba === true;
  }, [channelId, creditsData]);

  // Verifica se deve bloquear o envio - recalcula quando qualquer dependência muda
  const shouldBlockMessage = useMemo(() => {
    if (!channelId) {
      return false;
    }

    if (!creditsData) {
      return false;
    }

    if (isWabaBlockEnabled !== true) {
      return false;
    }

    if (isWabaChannel !== true) {
      return false;
    }

    if (isAt100PercentCredits !== true) {
      return false;
    }

    return true;
  }, [
    channelId,
    creditsData,
    isWabaBlockEnabled,
    isWabaChannel,
    isAt100PercentCredits,
  ]);

  return {
    isWabaBlockEnabled,
    loading,
    shouldBlockMessage,
    isAt100PercentCredits,
    isWabaChannel,
    refetch: fetchBlockStatus,
  };
}
